import L from 'leaflet';
import '@/assets/javascripts/Leaflet.Graticule.js';
import { computed, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';
import northArrow from '@/assets/images/north-arrow-image.png';
import { maxScale, minScale } from './useExportForm';

export default function useExportMap() {
  const store = useStore();

  function initGraticule(map) {
    const switchGrid = computed(
      () => store.state.exportStore.formData.switchGrid,
    );
    watch(switchGrid, (newVal) => {
      if (newVal) {
        addGraticule(map);
      } else {
        const graticule = store.state.exportStore.mapData.graticule;
        if (graticule) graticule.remove(map);
      }
    });
    if (switchGrid.value) {
      addGraticule(map);
    }
  }

  function initCompass(map) {
    const switchNorth = computed(
      () => store.state.exportStore.formData.switchNorth,
    );
    watch(switchNorth, (newVal) => {
      if (newVal) {
        addCompass(map);
      } else {
        const compass = document.querySelector('.compass');
        if (compass) compass.remove();
      }
    });
    if (switchNorth.value) {
      addCompass(map);
    }
  }

  function addCompass(map) {
    const compass = L.control({ position: 'topright' });
    compass.onAdd = function () {
      const div = L.DomUtil.create('div', 'compass');
      div.innerHTML = `<div style="font-size: 16px; text-align: center;">
        <img src="${northArrow}" style="height: 30px; display: block; margin: auto;" />
      </div>`;
      return div;
    };
    compass.addTo(map);
  }

  function addGraticule(map) {
    const graticule = L.latlngGraticule({
      showLabel: true,
      color: '#333',
      fontColor: '#333',
      opacity: 0.8,
      weight: 0.7,
      zoomInterval: [
        { start: 2, end: 2, interval: 20 },
        { start: 3, end: 3, interval: 10 },
        { start: 4, end: 4, interval: 8 },
        { start: 5, end: 5, interval: 4 },
        { start: 6, end: 6, interval: 2 },
        { start: 7, end: 7, interval: 1 },
        { start: 8, end: 8, interval: 0.4 },
        { start: 9, end: 9, interval: 0.2 },
        { start: 10, end: 10, interval: 0.1 },
        { start: 11, end: 11, interval: 0.06 },
        { start: 12, end: 12, interval: 0.025 },
        { start: 13, end: 13, interval: 0.015 },
        { start: 14, end: 14, interval: 0.008 },
        { start: 15, end: 15, interval: 0.004 },
        { start: 16, end: 16, interval: 0.002 },
        { start: 17, end: 17, interval: 0.001 },
        { start: 18, end: 18, interval: 0.0008 },
      ],
    }).addTo(map);
    store.commit('exportStore/updateMapData', { graticule });
  }

  function addWmsLayer(map) {
    const pixelRatio = 2;
    const m = computed(() =>
      store.getters['maps/topVisibleMap']
    );

    if (!m.value) return;

    L.tileLayer(m.value.wmsUrl, {
      tileSize: 256 / pixelRatio,
      zoomOffset: pixelRatio > 1 ? 1 : 0,
      detectRetina: false,
      r: pixelRatio > 1 ? '@2x' : '',
    }).addTo(map);
  }

  function bindZoomEvent(map) {
    map.on('zoomend', function() {
      const currentZoom = map.getZoom();
      const zoom = Math.min(currentZoom, maxScale);
      store.commit('exportStore/updateFormData', { scale: zoom });
    });
  }

  function bindMoveEvent(map) {
    map.on('moveend', () => {
      const bounds = map.getBounds();

      const southWest = bounds.getSouthWest();
      const northEast = bounds.getNorthEast();
  
      const bbox = {
        west: southWest.lng,
        south: southWest.lat,
        east: northEast.lng,
        north: northEast.lat,
      };
  
      store.commit('exportStore/updateMapData', { bbox });
    });
  }

  function initExportMap() {
    const {
      mapData: { initCenter },
      formData: { scale },
    } = store.state.exportStore;
    const center = store.state.engineStore.engine?.getCenter() || initCenter;
    console.log('center', center, 'scale', scale);
    const map = L.map('map', {
      keyboard: false,
    }).setView(center, scale);
    store.commit('exportStore/updateMapData', { mainMap: map });
    L.tileLayer(
      process.env.VUE_APP_BASEMAP_YINGXIANG,
      {
        maxZoom: maxScale,
        minZoom: minScale,
        subdomains: ['1', '2', '3'],
      },
    ).addTo(map);
    const zoomControl = map.zoomControl;
    map.removeControl(zoomControl);

    addWmsLayer(map);
    initCompass(map);
    initGraticule(map);
    bindZoomEvent(map);
    bindMoveEvent(map);

    L.control.scale({ metric: true, imperial: false }).addTo(map);
  }

  onUnmounted(() => {
    const { mainMap } = store.state.exportStore;
    if (mainMap) {
      mainMap.remove();
    }
  });

  return {
    initExportMap,
  };
}
